import { useCallback, useState } from "react";
import { AuthorizationContext, DEFAULT_CAPABILITIES } from "./AuthorizationContext";
import { Capability, PERMISSION, ROLE } from "./types";
import { useQuery } from "@apollo/client";
import { CAPABILITIES_QUERY, SECTION_LEGACY_IDS_QUERY } from "./queries";
import Button from "@/components/Button";
import PageMessage from "@/components/PageMessage";
import { getActiveBackendSource, isBackendSourceToggleAvailable, persistBackendSource } from "@/lib/backendSource";
import { buildCapabilitiesBootstrapError } from "./bootstrapErrorMessage";

type AuthorizationState = {
  capabilities: Capability;
  loaded: boolean;
  id: string | null;
  roles: string[];
  universityLegacyIds: string[];
  sectionLegacyIds: string[];
};

const INITIAL_AUTHORIZATION_STATE: AuthorizationState = {
  capabilities: DEFAULT_CAPABILITIES,
  loaded: false,
  id: null,
  roles: [],
  universityLegacyIds: [],
  sectionLegacyIds: []
};

export default function AuthorizationProvider({ children }: { children: React.ReactNode }) {
  const [state, setCapabilities] = useState<AuthorizationState>(INITIAL_AUTHORIZATION_STATE);

  const isSectionUser = state.roles.includes(ROLE.SECTION_STAFF) || state.roles.includes(ROLE.SECTION_ADMIN);

  const [bootstrapError, setBootstrapError] = useState<string | null>(null);

  const backendSourceToggleAvailable = isBackendSourceToggleAvailable();

  useQuery(CAPABILITIES_QUERY, {
    fetchPolicy: state.loaded ? "cache-first" : "network-only",
    skip: state.loaded || Boolean(bootstrapError),
    onCompleted({ me: { capabilities, id, roles, universityLegacyIds } }) {
      setBootstrapError(null);
      setCapabilities({ loaded: true, capabilities, id, roles, universityLegacyIds, sectionLegacyIds: [] });
    },
    onError(error) {
      if (backendSourceToggleAvailable) {
        setBootstrapError(buildCapabilitiesBootstrapError(error, getActiveBackendSource()));
        return;
      }

      setBootstrapError("Couldn't load your account");
    }
  });

  // Keep effective section claims from blocking the base authorization bootstrap during backend rollout.
  const { loading: sectionLegacyIdsQueryLoading } = useQuery(SECTION_LEGACY_IDS_QUERY, {
    fetchPolicy: "cache-first",
    skip: !state.loaded || !isSectionUser,
    onCompleted({ me: { sectionLegacyIds } }) {
      setCapabilities((currentState) => {
        if (
          currentState.sectionLegacyIds.length === sectionLegacyIds.length &&
          currentState.sectionLegacyIds.every((id, index) => id === sectionLegacyIds[index])
        ) {
          return currentState;
        }

        return { ...currentState, sectionLegacyIds };
      });
    }
  });

  const hasPermission = useCallback(
    (permission: PERMISSION) => {
      return state.capabilities.permissions.includes(permission);
    },
    [state.capabilities]
  );

  const hasRole = useCallback(
    (role: ROLE) => {
      return state.roles.includes(role);
    },
    [state.roles]
  );

  const hasUniversityAccess = useCallback(
    (universityLegacyId: string) => {
      return state.universityLegacyIds.includes(universityLegacyId);
    },
    [state.universityLegacyIds]
  );

  if (bootstrapError && !backendSourceToggleAvailable) {
    return (
      <PageMessage
        title="Couldn't load your account"
        message="Refresh the page to try again. If this keeps happening, contact PGA Technology."
        showLoadingIndicator={false}
      />
    );
  }

  if (
    bootstrapError &&
    backendSourceToggleAvailable &&
    import.meta.env.NODE_ENV !== "test" &&
    !import.meta.env.VITEST
  ) {
    const activeSource = getActiveBackendSource();

    return (
      <PageMessage title="Could not connect to API backend" message={bootstrapError} showLoadingIndicator={false}>
        {activeSource === "localhost" && (
          <Button
            onClick={() => {
              persistBackendSource("shared-dev");
              window.location.reload();
            }}
          >
            Use shared dev backend
          </Button>
        )}
      </PageMessage>
    );
  }

  // Render routes while MeQuery is in flight so page queries (e.g. People) overlap
  // capabilities bootstrap. Permission-gated actions and `hasRole` consumers must
  // fail closed until `loaded` is true.
  return (
    <AuthorizationContext.Provider
      value={{
        hasPermission,
        hasRole,
        hasUniversityAccess,
        sectionLegacyIdsLoading: state.loaded && isSectionUser && sectionLegacyIdsQueryLoading,
        ...state
      }}
    >
      {children}
    </AuthorizationContext.Provider>
  );
}
